// app/features/posthog/posthog.interface.ts

// Event names as constants to ensure consistency
export const POSTHOG_EVENTS = {
  // Core User Journey Events
  USER_SIGNED_UP: 'User Signed Up',
  SIGNUP_STARTED: 'Signup Started',
  USER_COMPLETED_ONBOARDING: 'User Completed Onboarding', 
  ORGANIZATION_CREATED: 'Organization Created',
  ORGANIZATION_CREATION_STARTED: 'Organization Creation Started',
  USER_CREATED_AI_WORKFLOW: 'User Created AI Workflow',
  WORKFLOW_GENERATION_STARTED: 'Workflow Generation Started',
  WORKFLOW_VIEWED: 'Workflow Viewed',
  
  // Newsletter Events
  WHATSAPP_TEMPLATE_SENT: 'WhatsApp Template Sent',
  FIRST_NEWSLETTER_DELIVERED: 'First Newsletter Delivered',
  NEWSLETTER_DELIVERED: 'Newsletter Delivered',
  
  // Template Events
  TEMPLATE_VIEWED: 'Template Viewed',
  TEMPLATE_DOWNLOADED: 'Template Downloaded',
  
  // Pricing & Subscription Events
  PRICING_PAGE_VIEWED: 'Pricing Page Viewed',
  PLAN_SELECTED: 'Plan Selected',
  CHECKOUT_STARTED: 'Checkout Started',
  CHECKOUT_COMPLETED: 'Checkout Completed',
  CHECKOUT_ABANDONED: 'Checkout Abandoned',
  SUBSCRIPTION_CREATED: 'Subscription Created',
  SUBSCRIPTION_UPDATED: 'Subscription Updated',
  SUBSCRIPTION_CANCELLED: 'Subscription Cancelled',
  SUBSCRIPTION_RENEWED: 'Subscription Renewed',
  PAYMENT_FAILED: 'Payment Failed',
  TRIAL_STARTED: 'Trial Started',
  TRIAL_ENDED: 'Trial Ended',
  
  // User Journey Events
  PAGE_VIEWED: 'Page Viewed',
  BUTTON_CLICKED: 'Button Clicked',
  FORM_SUBMITTED: 'Form Submitted',
  FEATURE_USED: 'Feature Used',
  ERROR_OCCURRED: 'Error Occurred',
} as const;

// Base event properties that all events should have
export interface BaseEventProperties {
  user_id?: string;
  email?: string;
  timestamp?: string;
  page_url?: string;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

// Specific event property interfaces
export interface UserSignedUpProperties extends BaseEventProperties {
  user_id: string;
  email: string;
  signup_method?: 'email' | 'google' | 'github';
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

export interface UserCompletedOnboardingProperties extends BaseEventProperties {
  user_id: string;
  email: string;
  employment_status?: string[];
  current_tools?: string[];
  onboarding_duration_seconds?: number;
}

export interface OrganizationCreatedProperties extends BaseEventProperties {
  user_id: string;
  email: string;
  organization_id: string;
  organization_name: string;
  industry_id?: string;
  employee_count_range?: string;
  workspace_type?: string;
  created_via?: 'web' | 'api';
}

export interface UserCreatedAIWorkflowProperties extends BaseEventProperties {
  user_id: string;
  email: string;
  workflow_id: string;
  problem_description: string;
  organization_id: string;
  level: string;
  desired_outcome?: string;
}

export interface SignupStartedProperties extends BaseEventProperties {
  page_url?: string;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

export interface OrganizationCreationStartedProperties extends BaseEventProperties {
  user_id: string;
  email: string;
}

export interface WorkflowGenerationStartedProperties extends BaseEventProperties {
  user_id: string;
  email: string;
  organization_id: string;
  problem_description: string;
  level: string;
  desired_outcome?: string;
}

export interface WorkflowViewedProperties extends BaseEventProperties {
  user_id: string;
  email: string;
  workflow_id: string;
  organization_id: string;
}

export interface WhatsAppTemplateSentProperties extends BaseEventProperties {
  user_id: string;
  email: string;
  template_name: string;
  edition_id?: string;
  is_first_newsletter: boolean;
}

export interface NewsletterDeliveredProperties extends BaseEventProperties {
  user_id: string;
  email: string;
  edition_id: string;
  edition_date: string;
  delivery_channel: 'whatsapp' | 'email';
  is_first_newsletter: boolean;
}

export interface TemplateViewedProperties extends BaseEventProperties {
  user_id?: string;
  email?: string;
  template_id: string;
  template_slug: string;
  template_title: string;
  template_category?: string;
  is_freebie: boolean;
}

export interface TemplateDownloadedProperties extends BaseEventProperties {
  user_id: string;
  email: string;
  template_id: string;
  template_slug: string;
  template_title: string;
  template_category?: string;
  file_id: string;
  file_name: string;
  file_type: string;
  is_freebie: boolean;
}

export interface PricingPageViewedProperties extends BaseEventProperties {
  user_id?: string;
  email?: string;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

export interface PlanSelectedProperties extends BaseEventProperties {
  user_id: string;
  email: string;
  plan_name: string;
  plan_id: string;
  price_id: string;
  price_amount: number;
  price_currency: string;
  billing_interval: 'month' | 'year';
}

export interface CheckoutStartedProperties extends BaseEventProperties {
  user_id: string;
  email: string;
  plan_name: string;
  plan_id: string;
  price_id: string;
  price_amount: number;
  price_currency: string;
  billing_interval: 'month' | 'year';
}

export interface CheckoutCompletedProperties extends BaseEventProperties {
  user_id: string;
  email: string;
  plan_name: string;
  plan_id: string;
  price_id: string;
  price_amount: number;
  price_currency: string;
  billing_interval: 'month' | 'year';
  transaction_id: string;
}

export interface SubscriptionCreatedProperties extends BaseEventProperties {
  user_id: string;
  email: string;
  subscription_id: string;
  plan_name: string;
  plan_id: string;
  price_id: string;
  price_amount: number;
  price_currency: string;
  billing_interval: 'month' | 'year';
  trial_days?: number;
}

export interface SubscriptionUpdatedProperties extends BaseEventProperties {
  user_id: string;
  email: string;
  subscription_id: string;
  old_plan_name?: string;
  new_plan_name: string;
  old_price_amount?: number;
  new_price_amount: number;
  change_type: 'upgrade' | 'downgrade' | 'plan_change';
}

export interface SubscriptionCancelledProperties extends BaseEventProperties {
  user_id: string;
  email: string;
  subscription_id: string;
  plan_name: string;
  cancellation_reason?: string;
  subscription_duration_days?: number;
}

export interface PaymentFailedProperties extends BaseEventProperties {
  user_id: string;
  email: string;
  subscription_id?: string;
  plan_name?: string;
  price_amount?: number;
  failure_reason?: string;
  payment_method_type?: string;
}

// Generic event properties for flexible tracking
export interface GenericEventProperties extends BaseEventProperties {
  event_category?: string;
  event_action?: string;
  event_label?: string;
  event_value?: number;
  custom_properties?: Record<string, any>;
}

// Union type for all possible event properties
export type EventProperties = 
  | UserSignedUpProperties
  | SignupStartedProperties
  | UserCompletedOnboardingProperties
  | OrganizationCreatedProperties
  | OrganizationCreationStartedProperties
  | UserCreatedAIWorkflowProperties
  | WorkflowGenerationStartedProperties
  | WorkflowViewedProperties
  | WhatsAppTemplateSentProperties
  | NewsletterDeliveredProperties
  | TemplateViewedProperties
  | TemplateDownloadedProperties
  | PricingPageViewedProperties
  | PlanSelectedProperties
  | CheckoutStartedProperties
  | CheckoutCompletedProperties
  | SubscriptionCreatedProperties
  | SubscriptionUpdatedProperties
  | SubscriptionCancelledProperties
  | PaymentFailedProperties
  | GenericEventProperties;

// PostHog client configuration
export interface PostHogConfig {
  apiKey: string;
  apiHost?: string;
  uiHost?: string;
  debug?: boolean;
  capturePageview?: boolean;
  capturePageleave?: boolean;
  captureExceptions?: boolean;
}

// Server-side PostHog configuration
export interface PostHogServerConfig {
  apiKey: string;
  host?: string;
  flushAt?: number;
  flushInterval?: number;
}

// Feature flags enum
export enum FeatureFlags {
  NEWS_ACCESS = 'news_access',
  // Add other flags here as needed
}